import { queueLoanWork } from "./loanQueue";
// Loan writes: optimistic, per-loan ordered (TanStack mutation scope `loan:<id>`), with
// rollback + error state on failure, conflict and interpretation toasts, paste batches and undo.
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "sonner";
import { api, ApiError, isRetryable } from "./api";
import { cacheEpoch, serverLoan, acceptServerLoan, addPending, clearPending, findCachedLoan, recordServer, refreshLoan, removeLoan } from "./loanCache";
import { markError, markSaved, markSaving } from "./saveState";
import { previewPrice } from "./price";
import { fieldLabel } from "./fields";
import { price3 } from "./format";
import { qk, useMe } from "./queries";
import type { BatchResponse, EditableField, FieldValue, Loan, LoanSet, Me, PatchResponse, UndoResponse } from "./types";

interface Ctx {
  qc: QueryClient;
  me: Me;
}

/** The value painted immediately. Price is sent raw (server normalises) but previewed locally. */
function optimisticSet(set: LoanSet): LoanSet {
  const out: LoanSet = {};
  for (const [f, v] of Object.entries(set) as [EditableField, FieldValue][]) {
    if (f === "price" && typeof v === "string") {
      if (v.trim() === "") out.price = null;
      else {
        const p = previewPrice(v);
        if (p.ok) out.price = p.price.toFixed(5);
      }
    } else out[f] = v;
  }
  return out;
}

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
    (Number(c) ^ (Math.random() * 16) >> (Number(c) / 4)).toString(16),
  );
}

function fmtValue(field: string, v: unknown): string {
  if (v === null || v === undefined || v === "") return "blank";
  if (field === "price") return price3(String(v));
  return String(v);
}

function handleAuth(qc: QueryClient, err: unknown) {
  if (err instanceof ApiError && err.status === 401) qc.invalidateQueries({ queryKey: qk.me });
}

function firstName(name: string | null | undefined) {
  return name ? name.split(/\s+/)[0] : null;
}

export async function patchLoan(ctx: Ctx, loan: Loan, set: LoanSet): Promise<void> {
  const { qc, me } = ctx;
  const id = loan.id;
  const fields = Object.keys(set) as EditableField[];
  if (!fields.length) return;
  const epoch = cacheEpoch();
  const expected: LoanSet = {};
  for (const f of fields) expected[f] = (loan[f] as FieldValue) ?? null;
  const token = addPending(id, optimisticSet(set));
  refreshLoan(qc, id, me, false);
  markSaving(id, fields);
  try {
    const res = await queueLoanWork([id], async () => {
      if (epoch !== cacheEpoch()) throw new Error("Session changed");
      let attempt = 0;
      while (true) {
        try { return await api<PatchResponse>(`/api/loans/${id}`, { method: "PATCH", body: { set, expected } }); }
        catch (e) {
          if (!isRetryable(e) || ++attempt > 3 || epoch !== cacheEpoch()) throw e;
          await new Promise((resolve) => window.setTimeout(resolve, Math.min(800 * 2 ** attempt, 8000)));
        }
      }
    });
    if (epoch !== cacheEpoch()) return;
    clearPending(id, token);
    recordServer(res.loan);
    refreshLoan(qc, id, me, false);
    markSaved(id, fields);
    void qc.invalidateQueries({ queryKey: qk.history(id) });
    if (res.interpreted?.price) toast(`Price saved as ${price3(res.interpreted.price)}`);
    for (const c of res.conflicts ?? []) {
      toast(`Replaced ${c.by ? `${firstName(c.by)}'s` : "a newer"} ${fieldLabel(c.field)} (${fmtValue(c.field, c.actual)})`, {
        duration: 10_000,
        action: { label: "Undo", onClick: () => void patchLoan(ctx, findCachedLoan(qc, id) ?? res.loan, { [c.field]: (c.actual ?? null) as FieldValue }) },
      });
    }
  } catch (err) {
    if (epoch !== cacheEpoch()) return;
    clearPending(id, token);
    refreshLoan(qc, id, me, false);
    handleAuth(qc, err);
    const msg = err instanceof Error ? err.message : "Couldn't save";
    markError(id, fields, msg);
    toast.error(`${fieldLabel(fields[0])} not saved`, { description: msg });
  }
}

export interface BatchEdit {
  loan: Loan;
  set: LoanSet;
}

export async function applyBatch(ctx: Ctx, edits: BatchEdit[], verb: "Pasted" | "Filled" | "Cleared" | "Updated"): Promise<void> {
  const { qc, me } = ctx;
  const live = edits.filter((e) => Object.keys(e.set).length > 0);
  if (!live.length) return;
  if (live.length === 1 && Object.keys(live[0].set).length === 1 ) {
    return patchLoan(ctx, live[0].loan, live[0].set);
  }
  const epoch = cacheEpoch();
  const batchId = uuid();
  const tokens = live.map((e) => {
    const token = addPending(e.loan.id, optimisticSet(e.set));
    refreshLoan(qc, e.loan.id, me, false);
    markSaving(e.loan.id, Object.keys(e.set));
    return token;
  });
  const settle = () =>
    live.forEach((e, i) => {
      clearPending(e.loan.id, tokens[i]);
      refreshLoan(qc, e.loan.id, me, false);
    });
  let res: BatchResponse;
  try {
    res = await queueLoanWork(live.map((e) => e.loan.id), () => {
      if (epoch !== cacheEpoch()) throw new Error("Session changed");
      return api<BatchResponse>("/api/loans/batch", {
        method: "POST", body: { batch_id: batchId, edits: live.map((e) => ({ id: e.loan.id, set: e.set })) },
      });
    });
    if (epoch !== cacheEpoch()) return;
  } catch (err) {
    if (epoch !== cacheEpoch()) return;
    settle();
    handleAuth(qc, err);
    const msg = err instanceof Error ? err.message : "Couldn't save";
    live.forEach((e) => markError(e.loan.id, Object.keys(e.set), msg));
    toast.error(`${verb} cells not saved`, { description: msg });
    return;
  }
  live.forEach((e, i) => clearPending(e.loan.id, tokens[i]));
  res.loans.forEach((l) => recordServer(l));
  live.forEach((e) => refreshLoan(qc, e.loan.id, me, false));
  const errKey = new Set(res.errors.map((e) => `${e.id}:${e.field}`));
  let applied = 0;
  for (const e of live) {
    const ok = Object.keys(e.set).filter((f) => !errKey.has(`${e.loan.id}:${f}`) && !errKey.has(`${e.loan.id}:null`));
    applied += ok.length;
    markSaved(e.loan.id, ok);
    qc.invalidateQueries({ queryKey: qk.history(e.loan.id) });
  }
  for (const e of res.errors) markError(e.id, e.field ? [e.field] : Object.keys(live.find((edit) => edit.loan.id === e.id)?.set ?? {}), e.message);
  if (applied > 0) {
    toast(`${verb} ${applied} cell${applied === 1 ? "" : "s"}`, {
      description: res.errors.length
        ? `${res.errors.length} skipped · ${fieldLabel(res.errors[0].field ?? "loan")}: ${res.errors[0].message}`
        : undefined,
      duration: 10_000,
      action: { label: "Undo", onClick: () => void undoBatch(ctx, batchId) },
    });
  } else if (res.errors.length) {
    toast.error(`Nothing ${verb.toLowerCase()}`, { description: `${fieldLabel(res.errors[0].field ?? "loan")}: ${res.errors[0].message}` });
  }
}

export async function undoBatch(ctx: Ctx, batchId: string, allowInsert = false) {
  const epoch = cacheEpoch();
  try {
    const res = await api<UndoResponse>(`/api/batches/${batchId}/undo`, { method: "POST" });
    if (epoch !== cacheEpoch()) return;
    res.loans.forEach((l) => acceptServerLoan(ctx.qc, l, ctx.me, allowInsert));
    res.loans.forEach((l) => ctx.qc.invalidateQueries({ queryKey: qk.history(l.id) }));
    toast.success(`Undone · ${res.reverted} change${res.reverted === 1 ? "" : "s"} reverted`, {
      description: res.skipped.length ? `${res.skipped.length} kept because they were edited again since` : undefined,
    });
  } catch (err) {
    toast.error("Undo failed", { description: err instanceof Error ? err.message : undefined });
  }
}

export async function createLoan(ctx: Ctx, body: LoanSet & { owner_user_id?: string }): Promise<Loan | null> {
  const epoch = cacheEpoch();
  try {
    const loan = await api<Loan>("/api/loans", { method: "POST", body });
    if (epoch !== cacheEpoch()) return null;
    acceptServerLoan(ctx.qc, loan, ctx.me, true);
    return loan;
  } catch (err) {
    handleAuth(ctx.qc, err);
    toast.error("Couldn't create the loan", { description: err instanceof Error ? err.message : undefined });
    return null;
  }
}

export async function deleteLoan(ctx: Ctx, loan: Loan): Promise<boolean> {
  const epoch = cacheEpoch();
  try {
    const batchId = uuid();
    await api<void>(`/api/loans/${loan.id}?batch_id=${batchId}`, { method: "DELETE" });
    if (epoch !== cacheEpoch()) return false;
    removeLoan(ctx.qc, loan.id, Math.max(loan.revision, serverLoan(loan.id)?.revision ?? 0) + 1);
    toast(`Deleted ${loan.file_name || "loan"}`, {
      duration: 10_000,
      action: { label: "Undo", onClick: () => void undoBatch(ctx, batchId, true) },
    });
    return true;
  } catch (err) {
    toast.error("Couldn't delete", { description: err instanceof Error ? err.message : undefined });
    return false;
  }
}

export function useLoanActions() {
  const qc = useQueryClient();
  const me = useMe();
  return useMemo(() => {
    const ctx = { qc, me };
    return {
      patch: (loan: Loan, set: LoanSet) => patchLoan(ctx, loan, set),
      batch: (edits: BatchEdit[], verb: "Pasted" | "Filled" | "Cleared" | "Updated") => applyBatch(ctx, edits, verb),
      undo: (batchId: string) => undoBatch(ctx, batchId),
      create: (body: LoanSet & { owner_user_id?: string }) => createLoan(ctx, body),
      remove: (loan: Loan) => deleteLoan(ctx, loan),
    };
  }, [qc, me]);
}
